import { Button, Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomFields, useSetUserFieldValues, useUserFieldValues, type CustomFieldValues } from '@/api/customFields';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { CustomFieldInputs } from '@/components/CustomFieldInputs';
import { useApiError } from '@/hooks/useApiError';

/** Profilfelder eines Mitglieds (TEAM-06). */
export function ProfileFieldsSection({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const enabled = ctx.hasFeature('USER_FIELDS');
  const { data: fields = [] } = useCustomFields(ctx.workspaceId, enabled);
  const { data } = useUserFieldValues(ctx.workspaceId, enabled ? userId : undefined);
  const save = useSetUserFieldValues(ctx.workspaceId, userId);
  const onError = useApiError();
  const [values, setValues] = useState<CustomFieldValues>({});
  useEffect(() => setValues((data?.values ?? {}) as CustomFieldValues), [data]);
  const userFields = fields.filter((f) => f.entity === 'USER' && !f.archived);
  if (!enabled || userFields.length === 0) return null;
  const canEdit = ctx.isAdmin || userId === ctx.user.id;

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {t('customFields.profileFields')}
      </Text>
      <CustomFieldInputs fields={userFields.map((field) => ({ field }))} values={values} onChange={setValues} disabled={!canEdit} isAdmin={ctx.isAdmin} />
      {canEdit && (
        <Button
          variant="light"
          size="xs"
          w="fit-content"
          loading={save.isPending}
          onClick={() => {
            const changed = Object.fromEntries(userFields.map((f) => [f.id, values[f.id] ?? null]));
            save.mutate(changed, { onError });
          }}
        >
          {t('customFields.saveProfileFields')}
        </Button>
      )}
    </Stack>
  );
}
